import type { GenericAttributeMapping } from '../../../components/AttributeMapping';

import { buildPayloadWithMappings } from './mappingReplacementUtils';
import { replacePlaceholdersWithIndex } from './placeholderIndexUtils';

interface HybridProcessingContext {
  originalTemplate: string;
  currentEntitlements: { value: string }[];
  stepFormData: Record<string, any>;
  allPrompts: Record<number, string>;
  currentPrompt?: string | null;
  currentIndex: number;
  accessType: 'AddAccess' | 'RemoveAccess';
}

/**
 * Get original entitlement count from template
 */
const getOriginalEntitlementCount = (template: string): number => {
  try {
    const parsed = JSON.parse(template) as { call?: unknown[] };

    return parsed.call?.length || 0;
  } catch {
    return 0;
  }
};

/**
 * Process payload with hybrid approach: existing entitlements (edit mode) + new entitlements (create mode)
 */
export const processHybridPayload = (
  context: HybridProcessingContext
): Record<string, unknown> => {
  const { originalTemplate, currentEntitlements, accessType } = context;

  const originalCount = getOriginalEntitlementCount(originalTemplate);
  const currentCount = currentEntitlements.length;

  // If no new entitlements, use normal edit mode processing
  if (currentCount <= originalCount) {
    return processEditModeSection(context);
  }

  // Hybrid processing: existing + new entitlements
  const existingEntitlements = currentEntitlements.slice(0, originalCount);
  const newEntitlements = currentEntitlements.slice(originalCount);

  // Process existing entitlements with edit mode logic (complete payload with prompts)
  const existingPayload = processEditModeSection({
    ...context,
    currentEntitlements: existingEntitlements,
  });

  // Process new entitlements with create mode logic (cumulative up to current)
  const newEntitlementsPayload = processNewEntitlementsSection({
    ...context,
    newEntitlements,
    originalCount,
  });

  // Merge both payloads
  return mergePayloads(existingPayload, newEntitlementsPayload, accessType);
};

/**
 * Process existing entitlements with edit mode logic
 */
const processEditModeSection = (
  context: HybridProcessingContext
): Record<string, unknown> => {
  const {
    originalTemplate,
    currentEntitlements,
    allPrompts,
    currentPrompt,
    currentIndex,
    accessType,
  } = context;

  // Build prompts array for existing entitlements
  const promptsArray: (string | null)[] = [];

  for (let i = 0; i < currentEntitlements.length; i += 1) {
    const prompt = allPrompts[i];

    promptsArray[i] = prompt && prompt.trim() !== '' ? prompt : null;
  }

  // Update current prompt if provided
  if (
    currentPrompt &&
    currentPrompt.trim() !== '' &&
    currentIndex < currentEntitlements.length
  ) {
    promptsArray[currentIndex] = currentPrompt;
  }

  // Process with placeholders replacement
  return replacePlaceholdersWithIndex(
    {
      multipleconnection: false,
      [`externalconnattvalue.${accessType}JSON`]: originalTemplate,
    },
    currentPrompt || null,
    currentEntitlements.length - 1,
    promptsArray
  );
};

/**
 * Process new entitlements with create mode logic
 */
const processNewEntitlementsSection = (
  context: HybridProcessingContext & {
    newEntitlements: { value: string }[];
    originalCount: number;
  }
): Record<string, unknown> => {
  const {
    originalTemplate,
    newEntitlements,
    stepFormData,
    currentIndex,
    originalCount,
    accessType,
  } = context;

  if (newEntitlements.length === 0) {
    return {
      multipleconnection: false,
      [`externalconnattvalue.${accessType}JSON`]: '{"call":[]}',
    };
  }

  // Extract base template structure for new entitlements
  try {
    const parsed = JSON.parse(originalTemplate) as { call?: unknown[] };
    const baseEntry = parsed.call?.[0];

    if (!baseEntry) {
      return {
        multipleconnection: false,
        [`externalconnattvalue.${accessType}JSON`]: '{"call":[]}',
      };
    }

    // Build mappings for new entitlements (only up to current index if in range)
    const allMappings: Record<number, GenericAttributeMapping[]> = {};
    const maxNewIndex =
      currentIndex >= originalCount
        ? currentIndex - originalCount
        : newEntitlements.length - 1;

    for (let i = 0; i <= maxNewIndex; i += 1) {
      const stepKey =
        accessType === 'AddAccess'
          ? `AddAccess_${originalCount + i}`
          : `RemoveAccess_${originalCount + i}`;
      const stepData = stepFormData[stepKey] as
        | { mappings?: GenericAttributeMapping[] }
        | undefined;

      if (stepData?.mappings) {
        allMappings[i] = stepData.mappings;
      }
    }

    // Process only new entitlements up to current index (create mode behavior)
    const entitlementsToProcess = newEntitlements.slice(0, maxNewIndex + 1);
    const jsonKey =
      accessType === 'AddAccess' ? 'AddAccessJSON' : 'RemoveAccessJSON';

    return buildPayloadWithMappings(
      originalTemplate,
      entitlementsToProcess,
      allMappings,
      jsonKey
    );
  } catch {
    return {
      multipleconnection: false,
      [`externalconnattvalue.${accessType}JSON`]: '{"call":[]}',
    };
  }
};

/**
 * Merge existing and new entitlements payloads
 */
const mergePayloads = (
  existingPayload: Record<string, unknown>,
  newPayload: Record<string, unknown>,
  accessType: 'AddAccess' | 'RemoveAccess'
): Record<string, unknown> => {
  const jsonKey = `externalconnattvalue.${accessType}JSON`;

  try {
    const existingData = JSON.parse(existingPayload[jsonKey] as string) as {
      call?: unknown[];
    };
    const newData = JSON.parse(newPayload[jsonKey] as string) as {
      call?: unknown[];
    };

    const mergedCall = [...(existingData.call || []), ...(newData.call || [])];

    return {
      multipleconnection: false,
      [jsonKey]: JSON.stringify({ call: mergedCall }),
    };
  } catch {
    // Fallback to existing payload if merge fails
    return existingPayload;
  }
};

/**
 * Check if current entitlements exceed original template count
 */
export const hasNewEntitlements = (
  originalTemplate: string,
  currentEntitlements: { value: string }[]
): boolean => {
  const originalCount = getOriginalEntitlementCount(originalTemplate);

  return currentEntitlements.length > originalCount;
};
